// The four patient-detail tabs (Vitals & I/O, eMAR, Tasks, Nursing Notes),
// each wired to its real backend endpoint. Exported individually.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Button, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, MenuItem, Chip, Checkbox, LinearProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import {
  getVitals, addVitals, getIntakeOutput, addIntakeOutput,
  getNotes, addNote, getNurseTasks, listOf,
} from "../api/nurseApi";
import { dateFilterParams, useNurseData } from "../NurseDataContext";
import { isTaskActionable, useFulfillmentNames } from "../hooks";
import { taskHeading, withoutDoctorNotes } from "../taskDisplay";
import { todayStr } from "../dateNav";
import { isMedicationTask } from "../medicationDisplay";
import { vitalValueSx } from "../vitalsThresholds";
import TprChart from "../components/TprChart";
import BpSpo2Chart from "../components/BpSpo2Chart";
import IoChart from "../components/IoChart";
import { O2_MODES, O2_MODE_ROOM_AIR, mostellerBsa } from "../clinicalScores";
import TaskFulfillmentModal from "../components/TaskFulfillmentModal";
import { istDayKey, formatIstTime, formatWallClockTime, wallClockDayKey } from "../../../utils/istDateTime";

const CONSCIOUSNESS = ["Alert", "Drowsy", "Confused", "Unresponsive"];

// When a vital / IO entry / note was recorded is an instant and arrives in UTC,
// so it is converted to ward time. A task's scheduled_datetime is the slot it
// was booked into and is shown as booked — see utils/istDateTime.
const fmt = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day} ${formatIstTime(dt)}` : "—";
};
const fmtSlot = (dt) => {
  const day = wallClockDayKey(dt);
  return day ? `${day} ${formatWallClockTime(dt)}` : "—";
};

// Fixed IO category lists (nursing-ops-2 #12) — an amendable draft, conditional
// on entry type. "Food (solid)" is the one gram-denominated category; grams
// never enter the ml fluid balance (CONTEXT.md "IO entry").
const IO_CATEGORIES = {
  intake: ["Oral fluids", "IV fluids", "Blood products", "Enteral/NG feed", "Food (solid)", "Other"],
  output: ["Urine", "Emesis", "Drain", "Stool", "NG aspirate", "Other"],
};
const isFoodCategory = (cat) => cat === "Food (solid)";

// "Nasal Cannula 4 L/min", or just the mode when it carries no flow (room air,
// or a mode recorded without one).
const o2Label = (row) => {
  if (!row.o2_mode) return "—";
  return row.o2_flow_lpm != null ? `${row.o2_mode} ${row.o2_flow_lpm} L/min` : row.o2_mode;
};

// Insert the "/" in a BP reading as the nurse types, the way OPD's Add Vitals
// field does (appointments/WellmessVitals.js). Deliberately NOT react-input-mask
// with mask="999/999" as OPD uses: that mask rejects a "/" typed at a digit
// position, so a 2-digit systolic ("90/60") is untypeable and comes out "906/0".
// Systolic under 100 is routine, so the separator position has to be whatever
// the nurse typed, falling back to after a full 3-digit systolic.
const formatBpWhileTyping = (input) => {
  const raw = String(input || "");
  const slash = raw.indexOf("/");
  if (slash >= 0) {
    const sys = raw.slice(0, slash).replace(/\D/g, "").slice(0, 3);
    const dia = raw.slice(slash + 1).replace(/\D/g, "").slice(0, 3);
    return `${sys}/${dia}`;
  }
  // No separator yet — add one once a 4th digit arrives, so backspacing back
  // past the "/" can still remove it.
  const digits = raw.replace(/\D/g, "").slice(0, 6);
  return digits.length > 3 ? `${digits.slice(0, 3)}/${digits.slice(3)}` : digits;
};

// Bounds for Record Vitals (DEFOPDIPD12). Two jobs: keep typos out of the chart,
// and keep values inside the column types VitalSign uses server-side —
// temperature / CVP / O₂ flow are Decimal(4,1) so 1000 overflows, and HR / SpO2 /
// RR are plain integers so a decimal there is rejected outright, not rounded.
// Ranges follow OPD's Add Vitals modal (appointments/WellmessVitals.js) where it
// has an equivalent field, and sit deliberately wider than the red bands in
// vitalsThresholds.js: an extreme reading is real and must stay recordable, a
// mistyped one must not.
const VITAL_RULES = {
  hr: { label: "HR", min: 20, max: 300, decimals: 0 },
  temp: { label: "Temp", min: 90, max: 110, decimals: 1 },
  spo2: { label: "SpO2", min: 0, max: 100, decimals: 0 },
  rr: { label: "RR", min: 0, max: 70, decimals: 0 },
  weight: { label: "Weight", min: 1, max: 300, decimals: 2 },
  height: { label: "Height", min: 20, max: 220, decimals: 1 },
  cvp: { label: "CVP", min: -10, max: 50, decimals: 1 },
  o2Flow: { label: "O₂ flow", min: 0, max: 100, decimals: 1 },
};

// The fields that count as "a reading was taken". Consciousness and O₂ mode have
// defaults, and height carries forward from the last entry, so none of the three
// means the nurse actually entered anything.
const MEASURED_KEYS = ["bp", "hr", "temp", "spo2", "rr", "weight", "cvp", "o2Flow"];

const checkNumber = (raw, r) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (!/^-?\d+(\.\d+)?$/.test(s)) return `${r.label} must be a number`;
  const places = (s.split(".")[1] || "").length;
  if (places > r.decimals) {
    return r.decimals === 0
      ? `${r.label} must be a whole number`
      : `${r.label} takes at most ${r.decimals} decimal place${r.decimals > 1 ? "s" : ""}`;
  }
  const n = Number(s);
  if (n < r.min || n > r.max) return `${r.label} must be between ${r.min} and ${r.max}`;
  return "";
};

// BP is stored as free text ("138/86"), so a half-typed "120/" would persist
// verbatim — it has to be complete before it can be saved.
const checkBp = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{1,3})\/(\d{1,3})$/);
  if (!m) return "BP must be in the format sys/dia, e.g. 120/80";
  const sys = Number(m[1]);
  const dia = Number(m[2]);
  if (sys < 40 || sys > 300) return "Systolic must be between 40 and 300";
  if (dia < 20 || dia > 200) return "Diastolic must be between 20 and 200";
  return "";
};

// Intake / output amounts are IntegerField columns server-side, so a decimal is
// rejected outright and a big enough number overflows — the same silent-failure
// path the vitals fields had. The backend already rejects non-integer and
// negative amounts with a plain message; the ceiling here is what stops an
// overflow ever reaching it. 20 L in one chart entry is not a real reading.
const IO_AMOUNT_MAX = 20000;
const checkIoAmount = (raw, unit) => {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  if (!/^\d+$/.test(s)) return `Amount must be a whole number of ${unit}`;
  const n = Number(s);
  if (n < 1 || n > IO_AMOUNT_MAX) return `Amount must be between 1 and ${IO_AMOUNT_MAX} ${unit}`;
  return "";
};

// A 200 from these endpoints can still carry a rejection. Most carry a plain
// sentence worth showing as-is; the blanket "<prefix> <raw psycopg error>"
// wrapper is not something to put in front of a nurse, so it goes to the console
// and the caller's own wording is shown instead.
const rejectionMessage = (res, rawPrefix, fallback) => {
  const msg = String(res?.message || "");
  if (msg.toLowerCase().includes("numeric field overflow")) {
    return "A vitals value is out of allowable range (values must be less than 1000 with at most 1 decimal place).";
  }
  if (msg && !msg.startsWith(rawPrefix)) return msg;
  if (msg) console.error("nurse-dashboard rejected:", msg);
  return fallback;
};

// { fieldKey: message } for every field that fails — empty when the form is fine.
const validateVitals = (v) => {
  const all = { bp: checkBp(v.bp) };
  Object.entries(VITAL_RULES).forEach(([k, r]) => {
    all[k] = checkNumber(v[k], r);
  });
  return Object.fromEntries(Object.entries(all).filter(([, msg]) => msg));
};

// ---------------------------------------------------------------- Vitals & I/O
export function VitalsTab({ admissionId, patientId, dateFrom, dateTo }) {
  const [history, setHistory] = useState([]);
  const [ioList, setIoList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [v, setV] = useState({
    bp: "", hr: "", temp: "", spo2: "", rr: "", cons: "Alert",
    weight: "", height: "", cvp: "", o2Mode: O2_MODE_ROOM_AIR, o2Flow: "",
  });
  const [io, setIo] = useState({ type: "intake", cat: "Oral fluids", amt: "" });
  // Errors are derived live so a corrected field clears itself, but stay hidden
  // until the nurse has tried to save — nothing is red while still typing.
  const [showErrors, setShowErrors] = useState(false);
  const errs = useMemo(() => validateVitals(v), [v]);
  const errText = (k) => (showErrors && errs[k]) || "";
  // Same idea for the I/O amount, whose unit follows the selected category.
  const [showIoErrors, setShowIoErrors] = useState(false);
  const ioUnit = isFoodCategory(io.cat) ? "g" : "ml";
  const ioErr = checkIoAmount(io.amt, ioUnit);
  const ioErrText = (showIoErrors && ioErr) || "";
  // Separate flags: the two panels save independently, so a vitals write must
  // not lock the I/O button. Without these a double-click writes two rows.
  const [savingVitals, setSavingVitals] = useState(false);
  const [savingIo, setSavingIo] = useState(false);
  const d = useMemo(() => dateFilterParams(dateFrom, dateTo), [dateFrom, dateTo]);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([getVitals(admissionId, d), getIntakeOutput(admissionId, d)])
      .then(([vr, ir]) => {
        const vitals = listOf(vr);
        setHistory(vitals);
        setIoList(listOf(ir));
        // Pre-fill height from the most recent reading that recorded one, so
        // BSA keeps working without the nurse re-entering an unchanging number.
        const lastHeight = vitals.find((row) => row.height_cm != null)?.height_cm;
        if (lastHeight != null) setV((s) => (s.height ? s : { ...s, height: String(lastHeight) }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [admissionId, d]);
  useEffect(reload, [reload]);

  const saveVitals = async () => {
    setShowErrors(true);
    if (Object.keys(errs).length) return;
    if (!MEASURED_KEYS.some((k) => String(v[k] ?? "").trim())) {
      toast.error("Enter at least one reading before saving");
      return;
    }
    setSavingVitals(true);
    try {
      const res = await addVitals({
        admission_id: admissionId, patient_id: patientId,
        blood_pressure: v.bp || null, heart_rate: v.hr || null, temperature: v.temp || null,
        oxygen_saturation: v.spo2 || null, respiratory_rate: v.rr || null, consciousness: v.cons,
        weight: v.weight || null, height_cm: v.height || null, cvp_cmh2o: v.cvp || null,
        o2_mode: v.o2Mode || null,
        // Room air carries no flow rate; the backend drops one anyway.
        o2_flow_lpm: v.o2Mode === O2_MODE_ROOM_AIR ? null : (v.o2Flow || null),
      });
      // Flat envelope — a 200 can still carry a rejection, and clearing the form
      // on one is what made a failed save look like a successful one.
      if (res?.status !== "Success") {
        toast.error(rejectionMessage(
          res, "Could not record vitals:",
          "Could not record the vitals — please check the values",
        ));
        return;
      }
      toast.success("Vitals recorded");
      // Height carries forward — it does not change between readings, and
      // re-typing it every time is how it ends up missing (and BSA with it).
      setV((s) => ({
        bp: "", hr: "", temp: "", spo2: "", rr: "", cons: "Alert",
        weight: "", cvp: "", o2Mode: O2_MODE_ROOM_AIR, o2Flow: "", height: s.height,
      }));
      setShowErrors(false);
      reload();
    } catch (e) {
      const serverMsg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        "";
      if (
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("numeric field overflow")
      ) {
        toast.error(
          "A vitals value is out of allowable range (values must be less than 1000 with at most 1 decimal place)."
        );
      } else {
        toast.error(serverMsg || "Could not record the vitals");
      }
    } finally {
      setSavingVitals(false);
    }
  };
  const saveIO = async () => {
    setShowIoErrors(true);
    if (ioErr) return;
    if (!String(io.amt ?? "").trim()) {
      toast.error("Enter an amount before saving");
      return;
    }
    // Food (solid) is recorded in grams (amount_g); every other category in ml.
    const amount = isFoodCategory(io.cat)
      ? { amount_g: io.amt, amount_ml: null }
      : { amount_ml: io.amt };
    setSavingIo(true);
    try {
      const res = await addIntakeOutput({ admission_id: admissionId, patient_id: patientId, type: io.type, category: io.cat, ...amount });
      if (res?.status !== "Success") {
        toast.error(rejectionMessage(
          res, "Could not record intake/output:",
          "Could not record the entry — please check the amount",
        ));
        return;
      }
      toast.success("Entry recorded");
      setIo((s) => ({ ...s, amt: "" }));
      setShowIoErrors(false);
      reload();
    } catch (e) {
      const serverMsg =
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        e?.message ||
        "";
      toast.error(serverMsg || "Could not record the entry");
    } finally {
      setSavingIo(false);
    }
  };

  // Live BSA echo while entering height/weight — derived, never stored.
  const bsa = mostellerBsa(v.height, v.weight);

  // Fluid balance is ml-only: food (gram) entries never enter these totals.
  const intakeTotal = ioList.filter((e) => e.type === "intake").reduce((s, e) => s + (Number(e.amount_ml) || 0), 0);
  const outputTotal = ioList.filter((e) => e.type === "output").reduce((s, e) => s + (Number(e.amount_ml) || 0), 0);
  const foodTotal = ioList.reduce((s, e) => s + (Number(e.amount_g) || 0), 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Record Vitals</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField size="small" fullWidth label="BP (mmHg)" value={v.bp} onChange={(e) => setV({ ...v, bp: formatBpWhileTyping(e.target.value) })} placeholder="120/80" error={!!errText("bp")} helperText={errText("bp")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="HR (bpm)" value={v.hr} onChange={(e) => setV({ ...v, hr: e.target.value })} inputProps={{ inputMode: "numeric" }} error={!!errText("hr")} helperText={errText("hr")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="Temp (°F)" value={v.temp} onChange={(e) => setV({ ...v, temp: e.target.value })} inputProps={{ inputMode: "decimal" }} error={!!errText("temp")} helperText={errText("temp")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="SpO2 (%)" value={v.spo2} onChange={(e) => setV({ ...v, spo2: e.target.value })} inputProps={{ inputMode: "numeric" }} error={!!errText("spo2")} helperText={errText("spo2")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="RR (/min)" value={v.rr} onChange={(e) => setV({ ...v, rr: e.target.value })} inputProps={{ inputMode: "numeric" }} error={!!errText("rr")} helperText={errText("rr")} /></Grid>
            <Grid item xs={6}>
              <TextField size="small" fullWidth select label="Consciousness" value={v.cons} onChange={(e) => setV({ ...v, cons: e.target.value })}>
                {CONSCIOUSNESS.map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="Weight (kg)" value={v.weight} onChange={(e) => setV({ ...v, weight: e.target.value })} inputProps={{ inputMode: "decimal" }} error={!!errText("weight")} helperText={errText("weight")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="Height (cm)" value={v.height} onChange={(e) => setV({ ...v, height: e.target.value })} inputProps={{ inputMode: "decimal" }} error={!!errText("height")} helperText={errText("height") || (bsa ? `BSA ${bsa.toFixed(2)} m²` : " ")} /></Grid>
            <Grid item xs={6}><TextField size="small" fullWidth label="CVP (cmH₂O)" value={v.cvp} onChange={(e) => setV({ ...v, cvp: e.target.value })} inputProps={{ inputMode: "decimal" }} error={!!errText("cvp")} helperText={errText("cvp")} /></Grid>
            <Grid item xs={6}>
              <TextField size="small" fullWidth select label="O₂ therapy" value={v.o2Mode} onChange={(e) => setV({ ...v, o2Mode: e.target.value, o2Flow: e.target.value === O2_MODE_ROOM_AIR ? "" : v.o2Flow })}>
                {O2_MODES.map((m) => (<MenuItem key={m} value={m}>{m}</MenuItem>))}
              </TextField>
            </Grid>
            {v.o2Mode !== O2_MODE_ROOM_AIR && (
              <Grid item xs={6}><TextField size="small" fullWidth label="O₂ flow (L/min)" value={v.o2Flow} onChange={(e) => setV({ ...v, o2Flow: e.target.value })} inputProps={{ inputMode: "decimal" }} error={!!errText("o2Flow")} helperText={errText("o2Flow")} /></Grid>
            )}
            <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={saveVitals} disabled={savingVitals}>{savingVitals ? "Saving…" : "Save Vitals"}</Button></Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="subtitle1">Intake / Output</Typography>
            <Stack direction="row" spacing={1}>
              <Chip size="small" color="info" label={`In: ${intakeTotal} ml`} />
              <Chip size="small" color="warning" label={`Out: ${outputTotal} ml`} />
              <Chip size="small" variant="outlined" label={`Net: ${intakeTotal - outputTotal} ml`} />
              {foodTotal > 0 && <Chip size="small" color="success" variant="outlined" label={`Food: ${foodTotal} g`} />}
            </Stack>
          </Stack>
          <Grid container spacing={1.5}>
            <Grid item xs={4}>
              <TextField
                size="small" fullWidth select label="Type" value={io.type}
                onChange={(e) => setIo({ type: e.target.value, cat: IO_CATEGORIES[e.target.value][0], amt: "" })}
              >
                <MenuItem value="intake">Intake</MenuItem>
                <MenuItem value="output">Output</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" fullWidth select label="Category" value={io.cat} onChange={(e) => setIo({ ...io, cat: e.target.value })}>
                {IO_CATEGORIES[io.type].map((c) => (<MenuItem key={c} value={c}>{c}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                size="small" fullWidth value={io.amt}
                label={`Amount (${ioUnit})`}
                onChange={(e) => setIo({ ...io, amt: e.target.value })}
                inputProps={{ inputMode: "numeric" }}
                error={!!ioErrText} helperText={ioErrText}
              />
            </Grid>
            <Grid item xs={12}><Button variant="contained" startIcon={<AddIcon />} onClick={saveIO} disabled={savingIo}>{savingIo ? "Saving…" : "Add Entry"}</Button></Grid>
          </Grid>
        </Paper>
      </Grid>

      {loading && <Grid item xs={12}><LinearProgress /></Grid>}

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1">TPR Trend</Typography></Box>
          <Box sx={{ px: 2, pb: 2 }}><TprChart vitals={history} /></Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1">BP &amp; SpO₂ Trend</Typography></Box>
          <Box sx={{ px: 2, pb: 2 }}><BpSpo2Chart vitals={history} /></Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1">Vitals History</Typography></Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Time</TableCell><TableCell>BP</TableCell><TableCell>HR</TableCell><TableCell>Temp</TableCell><TableCell>SpO2</TableCell><TableCell>RR</TableCell><TableCell>CVP</TableCell><TableCell>O₂</TableCell><TableCell>Wt</TableCell><TableCell>Consciousness</TableCell><TableCell>By</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{fmt(row.recorded_at)}</TableCell><TableCell sx={vitalValueSx("bp", row.blood_pressure)}>{row.blood_pressure}</TableCell><TableCell sx={vitalValueSx("hr", row.heart_rate)}>{row.heart_rate}</TableCell><TableCell sx={vitalValueSx("temp", row.temperature)}>{row.temperature}</TableCell><TableCell sx={vitalValueSx("spo2", row.oxygen_saturation)}>{row.oxygen_saturation}</TableCell><TableCell sx={vitalValueSx("rr", row.respiratory_rate)}>{row.respiratory_rate}</TableCell><TableCell>{row.cvp_cmh2o ?? "—"}</TableCell><TableCell>{o2Label(row)}</TableCell><TableCell>{row.weight ?? "—"}</TableCell><TableCell>{row.consciousness}</TableCell><TableCell>{row.recorded_by_name}</TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && (<TableRow><TableCell colSpan={11} align="center" sx={{ py: 3, color: "text.secondary" }}>No vitals recorded yet.</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1">I/O Chart</Typography></Box>
          <Box sx={{ px: 2, pb: 2 }}><IoChart entries={ioList} /></Box>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2 }}><Typography variant="subtitle1">I/O Log</Typography></Box>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                <TableCell>Time</TableCell><TableCell>Type</TableCell><TableCell>Category</TableCell><TableCell align="right">Amount</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {ioList.map((e) => (
                  <TableRow key={e.id} hover>
                    <TableCell>{fmt(e.recorded_at)}</TableCell>
                    <TableCell><Chip size="small" color={e.type === "intake" ? "info" : "warning"} label={e.type} /></TableCell>
                    <TableCell>{e.category}</TableCell>
                    <TableCell align="right">{e.amount_g != null && e.amount_g !== "" ? `${e.amount_g} g` : `${e.amount_ml ?? "—"} ml`}</TableCell>
                  </TableRow>
                ))}
                {ioList.length === 0 && (<TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>No I/O entries.</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
}

// The eMAR tab that lived here is now views/MedicationsTab.js, where the
// administration record sits alongside the order book it records against.

// ---------------------------------------------------------------------- Tasks
// The administration record: every scheduled task for this patient (medication
// doses, dressings, etc.), each recordable through its fulfillment form, with a
// live progress rollup per order from care_plan_data_summary/.
const taskLabel = (t, fulfillmentNames) => {
  const med = t.order_details?.medication;
  if (med?.drug_name) return [med.drug_name, med.route, med.frequency].filter(Boolean).join(" · ");
  // task_type is the care plan's template name ("Task"), so it names nothing
  // on its own — prefer what the doctor wrote, then the form's name (DEFIPD082).
  return taskHeading(t, fulfillmentNames);
};

// scheduled_datetime is an ISO string; the date half sorts lexicographically,
// so plain string compares are enough to window by day.
const dayOf = (t) => String(t.scheduled_datetime || "").slice(0, 10);
const MIN_DAY = "0000-01-01";
const MAX_DAY = "9999-12-31";

export function TasksTab({ admissionId, dateFrom, dateTo, onOpenMedications }) {
  const { currentNurseId } = useNurseData();
  const fulfillmentNames = useFulfillmentNames();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  // Every task for this admission, windowed client-side. This tab used to ask
  // the server for a single day and ignore the dateFrom/dateTo it was passed,
  // so clicking a patient the dashboard flagged as "21 overdue" landed on an
  // empty tab — those rows were never fetched at all. Only the window and the
  // still-outstanding overdue rows are RENDERED, so the thousand-row scroll
  // this tab used to be does not come back.
  const reload = useCallback(() => {
    setLoading(true);
    getNurseTasks({ admission_id: admissionId })
      .then((tr) => setTasks(withoutDoctorNotes(listOf(tr))))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [admissionId]);
  useEffect(reload, [reload]);

  // The window every other view reports on: the dashboard's range when one is
  // set, otherwise today (EmarOverview's convention).
  const hasRange = Boolean(dateFrom || dateTo);
  const windowStart = hasRange ? dateFrom || MIN_DAY : todayStr();
  const windowEnd = hasRange ? dateTo || MAX_DAY : todayStr();
  const inWindow = useCallback(
    (t) => {
      const d = dayOf(t);
      return Boolean(d) && d >= windowStart && d <= windowEnd;
    },
    [windowStart, windowEnd]
  );
  // Outstanding work scheduled before the window — the backlog the dashboard
  // counts as overdue. Kept in its own list so "what is due now" stays readable.
  const isOverdue = useCallback(
    (t) => isTaskActionable(t) && Boolean(dayOf(t)) && dayOf(t) < windowStart,
    [windowStart]
  );

  // Medication doses live in the Medications tab now, recorded through the
  // administration sheet. Two screens that both give a dose is exactly the
  // confusion this split removes — what's left here is the other nursing work.
  const nursingTasks = useMemo(
    () => tasks.filter((t) => !isMedicationTask(t) && inWindow(t)),
    [tasks, inWindow]
  );
  const overdueTasks = useMemo(
    () => tasks.filter((t) => !isMedicationTask(t) && isOverdue(t)),
    [tasks, isOverdue]
  );
  // How many doses were kept out of both lists. An empty list alone reads as
  // "this patient has no work"; the count plus a way through says "the work is
  // on the next tab", which is the actual truth.
  const medicationCount = useMemo(
    () => tasks.filter((t) => isMedicationTask(t) && (inWindow(t) || isOverdue(t))).length,
    [tasks, inWindow, isOverdue]
  );

  const isDone = (t) => String(t.status).toLowerCase() === "completed";
  // Counted off the list on screen, so the tally and the rows can never
  // disagree (the care-plan rollup spans every day, not the one shown).
  const totals = useMemo(() => {
    const count = (s) => nursingTasks.filter((t) => String(t.status).toLowerCase() === s).length;
    return {
      total: nursingTasks.length,
      completed: count("completed"),
      pending: count("pending"),
      in_progress: count("in_progress"),
      missed: count("missed"),
    };
  }, [nursingTasks]);

  const pct = totals.total ? Math.round((totals.completed / totals.total) * 100) : 0;

  // Shared by the overdue list and the window list — the same row means the
  // same thing in both, and an overdue task stays recordable.
  const renderTask = (t) => {
    const done = isDone(t);
    const actionable = isTaskActionable(t);
    return (
      <Paper
        key={t.id}
        variant="outlined"
        onClick={() => actionable && t.id != null && setActive(t)}
        sx={{ p: 1.5, borderRadius: 2, cursor: actionable ? "pointer" : "default", opacity: actionable || done ? 1 : 0.6 }}
      >
        <Stack direction="row" sx={{ alignItems: "center" }}>
          <Checkbox checked={done} disabled readOnly />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ textDecoration: done ? "line-through" : "none", fontWeight: 600 }}>
              {taskLabel(t, fulfillmentNames)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t.task_type}{t.time_slot_label ? ` · ${t.time_slot_label}` : ""} · {fmtSlot(t.scheduled_datetime)}
            </Typography>
          </Box>
          {/* The "meds not received" chip is gone — nothing ever marks an
              order Received, so it showed on every task (MAR spec D5). */}
          <Chip
            size="small"
            variant="outlined"
            label={done ? "recorded" : (t.status || "pending")}
            color={done ? "success" : t.status === "in_progress" ? "warning" : "default"}
          />
        </Stack>
      </Paper>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }} useFlexGap>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Nursing Tasks</Typography>
        <Typography variant="body2" color="text.secondary">{totals.completed}/{totals.total} done</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1, mb: 1 }} />
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip size="small" color="success" variant="outlined" label={`Completed ${totals.completed}`} />
        {totals.in_progress > 0 && <Chip size="small" color="warning" variant="outlined" label={`In progress ${totals.in_progress}`} />}
        <Chip size="small" variant="outlined" label={`Pending ${totals.pending}`} />
        {totals.missed > 0 && <Chip size="small" color="error" variant="outlined" label={`Missed ${totals.missed}`} />}
      </Stack>

      {medicationCount > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, borderRadius: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
            {medicationCount} medication {medicationCount === 1 ? "dose is" : "doses are"} not
            listed here — they are given from the Medications tab.
          </Typography>
          {onOpenMedications && (
            <Button size="small" onClick={onOpenMedications}>Open Medications</Button>
          )}
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {overdueTasks.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", mb: 1, flexWrap: "wrap" }}>
            <Typography variant="subtitle2" color="error">Overdue ({overdueTasks.length})</Typography>
            <Typography variant="caption" color="text.secondary">
              still outstanding from before {hasRange ? "this date range" : "today"}
            </Typography>
          </Stack>
          <Stack spacing={1}>{overdueTasks.map(renderTask)}</Stack>
        </Box>
      )}

      <Stack spacing={1}>
        {nursingTasks.map(renderTask)}
        {!loading && nursingTasks.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderRadius: 2, color: NURSE_COLORS.textSecondary }}>
            {medicationCount > 0
              ? `No non-medication nursing tasks ${hasRange ? "in this date range" : "today"}.`
              : `No nursing tasks ${hasRange ? "in this date range" : "today"}.`}
          </Paper>
        )}
      </Stack>

      <TaskFulfillmentModal
        open={!!active}
        task={active}
        performedById={currentNurseId}
        onClose={() => setActive(null)}
        onFulfilled={reload}
      />
    </Box>
  );
}

// ------------------------------------------------------------- Nursing Notes
const TEMPLATES = [
  { label: "Stable & comfortable", text: "Patient stable, comfortable, vitals within normal limits. No acute complaints." },
  { label: "Post-op routine", text: "Post-op POD-{n}. Wound site clean, dry, intact. Pain managed with analgesia. Ambulated with assistance." },
  { label: "Febrile episode", text: "Febrile. Tepid sponging done. Paracetamol administered per orders. Will continue to monitor." },
  { label: "Shift handover", text: "Handover at end of shift. Patient stable. Pending tasks noted. Family informed." },
];

export function NotesTab({ admissionId, patientId, dateFrom, dateTo }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [tpl, setTpl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const d = useMemo(() => dateFilterParams(dateFrom, dateTo), [dateFrom, dateTo]);

  const reload = useCallback(() => {
    setLoading(true);
    getNotes(admissionId, d).then((r) => setNotes(listOf(r))).catch(() => {}).finally(() => setLoading(false));
  }, [admissionId, d]);
  useEffect(reload, [reload]);

  const apply = (label) => { setTpl(label); const t = TEMPLATES.find((x) => x.label === label); if (t) setText(t.text); };
  const save = async () => {
    const content = text.trim();
    if (!content) {
      toast.error("Write a note before saving");
      return;
    }
    setSaving(true);
    try {
      const res = await addNote({ admission_id: admissionId, patient_id: patientId, content, note_type: tpl || null });
      if (res?.status !== "Success") {
        toast.error(rejectionMessage(res, "Could not save note:", "Could not save the note"));
        return;
      }
      toast.success("Note saved");
      setText(""); setTpl(""); reload();
    } catch (e) {
      toast.error("Could not save the note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Add Nursing Note</Typography>
        <Stack spacing={1.5}>
          <TextField select size="small" label="Template (optional)" value={tpl} onChange={(e) => apply(e.target.value)}>
            <MenuItem value="">— None —</MenuItem>
            {TEMPLATES.map((t) => (<MenuItem key={t.label} value={t.label}>{t.label}</MenuItem>))}
          </TextField>
          <TextField multiline rows={4} fullWidth value={text} onChange={(e) => setText(e.target.value)} placeholder="Write your nursing observation..." />
          <Box><Button variant="contained" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Note"}</Button></Box>
        </Stack>
      </Paper>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent Notes</Typography>
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <Stack spacing={1}>
        {notes.map((n) => (
          <Paper key={n.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary">{fmt(n.recorded_at)} · {n.author_name || "Nurse"}{n.note_type ? ` · ${n.note_type}` : ""}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>{n.content}</Typography>
          </Paper>
        ))}
        {!loading && notes.length === 0 && (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderRadius: 2, color: "text.secondary" }}>No nursing notes yet.</Paper>
        )}
      </Stack>
    </Box>
  );
}
